/**
 * CorruptionReporterService - S022: Data Integrity Monitoring
 *
 * Frontend service for reporting Y.js/data corruption to the backend.
 *
 * SOLID Principles:
 * - S: Orchestrates reporting only, delegates to specialized components
 * - O: Open for extension via interfaces
 * - L: N/A (no inheritance)
 * - I: Uses focused interfaces (IOfflineStorage, IEnvironmentDetector)
 * - D: Depends on abstractions, not concretions
 *
 * Features:
 * - Fire-and-forget API (non-blocking)
 * - Offline queue with pluggable storage
 * - Automatic client environment detection
 * - Debouncing to prevent duplicate reports
 */

import { DebugLogger } from '@affine/debug';
import {
  type ReportCorruptionInput,
  reportCorruptionMutation,
} from '@affine/graphql';
import { Service } from '@toeverything/infra';

import { GraphQLService } from '../cloud';
import type { BrowserEnvironmentDetector } from './environment-detector';
import type {
  CorruptionReportOptions,
  YjsErrorReportOptions,
} from './interfaces';
import type { LocalStorageOfflineQueue } from './offline-storage';
import { bytesToHex, classifyYjsError } from './yjs-error-classifier';

const logger = new DebugLogger('corruption-reporter');

// Debounce time for same doc/error reports (5 minutes)
const DEBOUNCE_MS = 5 * 60 * 1000;

export class CorruptionReporterService extends Service {
  private readonly recentReports = new Map<string, number>();

  constructor(
    private readonly graphqlService: GraphQLService,
    private readonly offlineStorage: LocalStorageOfflineQueue,
    private readonly environmentDetector: BrowserEnvironmentDetector
  ) {
    super();
    this.flushOfflineQueue().catch(() => {
      // Ignore flush errors - will retry on next report
    });
  }

  /**
   * Report a corruption event (fire-and-forget).
   * Non-blocking - will queue if offline.
   */
  report(options: CorruptionReportOptions): void {
    if (this.shouldDebounce(options)) {
      logger.debug('Skipping duplicate corruption report (debounced)', {
        docId: options.docId,
        errorType: options.errorType,
      });
      return;
    }

    this.markReported(options);

    const input = this.buildReportInput(options);

    logger.info('Reporting corruption', {
      workspaceId: input.workspaceId,
      docId: input.docId,
      errorType: input.errorType,
      binLength: input.binLength,
    });

    // Fire-and-forget
    this.sendReport(input).catch(() => {
      this.queueReport(input);
    });
  }

  /**
   * Report corruption from a caught Y.js error.
   * Convenience method for common Y.js error handling.
   */
  reportYjsError(options: YjsErrorReportOptions): void {
    this.report({
      workspaceId: options.workspaceId,
      docId: options.docId,
      errorType: classifyYjsError(options.error),
      errorMessage: options.error.message,
      operation: options.operation,
      binLength: options.binaryData?.length ?? 0,
      binPreview: options.binaryData,
      docTitleSnapshot: options.docTitle,
    });
  }

  private buildReportInput(
    options: CorruptionReportOptions
  ): ReportCorruptionInput {
    const env = this.environmentDetector.detect();

    return {
      workspaceId: options.workspaceId,
      docId: options.docId,
      clientTimestamp: new Date().toISOString(),
      clientTimezone: this.environmentDetector.getTimezone(),
      errorType: options.errorType,
      errorMessage: options.errorMessage.slice(0, 2000),
      operation: options.operation,
      wasOnline: this.environmentDetector.isOnline(),
      binLength: options.binLength,
      binPreview: options.binPreview
        ? bytesToHex(options.binPreview.slice(0, 100))
        : undefined,
      docTitleSnapshot: options.docTitleSnapshot?.slice(0, 200),
      browser: env.browser,
      os: env.os,
      appVersion: env.appVersion,
    };
  }

  private async sendReport(input: ReportCorruptionInput): Promise<void> {
    try {
      await this.graphqlService.gql({
        query: reportCorruptionMutation,
        variables: { input },
      });
      logger.debug('Corruption report sent successfully', {
        docId: input.docId,
      });
    } catch (error) {
      logger.warn('Failed to send corruption report', {
        error,
        docId: input.docId,
      });
      throw error;
    }
  }

  private queueReport(input: ReportCorruptionInput): void {
    const queue = this.offlineStorage.getQueue();

    queue.push({
      input: {
        ...input,
        clientTimestamp: input.clientTimestamp as string,
      },
      queuedAt: Date.now(),
    });

    this.offlineStorage.saveQueue(queue);
    logger.debug('Corruption report queued for later', { docId: input.docId });
  }

  private async flushOfflineQueue(): Promise<void> {
    const queue = this.offlineStorage.getQueue();
    if (queue.length === 0) return;

    logger.info(`Flushing ${queue.length} queued corruption reports`);

    const remaining = [];

    for (const item of queue) {
      try {
        await this.sendReport({
          ...item.input,
          clientTimestamp: item.input.clientTimestamp,
        });
      } catch {
        remaining.push(item);
      }
    }

    this.offlineStorage.saveQueue(remaining);

    if (remaining.length > 0) {
      logger.warn(`${remaining.length} corruption reports still queued`);
    }
  }

  private shouldDebounce(options: CorruptionReportOptions): boolean {
    const key = this.getReportKey(options);
    const now = Date.now();
    const lastReport = this.recentReports.get(key);

    return lastReport !== undefined && now - lastReport < DEBOUNCE_MS;
  }

  private markReported(options: CorruptionReportOptions): void {
    const key = this.getReportKey(options);
    this.recentReports.set(key, Date.now());
    this.cleanupRecentReports();
  }

  private getReportKey(options: CorruptionReportOptions): string {
    return `${options.workspaceId}:${options.docId}:${options.errorType}:${options.errorMessage.slice(0, 50)}`;
  }

  private cleanupRecentReports(): void {
    const now = Date.now();
    for (const [key, timestamp] of this.recentReports) {
      if (now - timestamp > DEBOUNCE_MS) {
        this.recentReports.delete(key);
      }
    }
  }
}
